// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what Home is showing right now, sent to the assistant just before a
// question asked from Home, so "message him" means the person in the Worth a
// look note or the Sorted list. The feed keeps this up to date; the page
// pushes it on send.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {HomeDigestResult} from '@app/features/konvo_today/state/HomeDigest';
import type {HomeFeedItem, HomeFeedTag} from '@app/features/konvo_today/utils/HomeFeedModel';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable} from 'mobx';

export interface HomeContextRow {
	source: HomeFeedItem['source'];
	name: string;
	preview: string;
	tag: string | null;
	unread: number;
	conversation_id: string | null;
	message_id: string | null;
}

const MAX_ROWS = 14;
/** The push must not hold the message back for long; the assistant still answers without it. */
const PUSH_TIMEOUT_MS = 1500;

function tagText(tag: HomeFeedTag | undefined): string | null {
	if (!tag) return null;
	switch (tag.kind) {
		case 'mention':
			return 'Mentioned you';
		case 'needs_reply':
			return 'Needs reply';
		case 'waiting':
			return `Waiting ${tag.days} days`;
		case 'security':
			return 'Security';
	}
}

class HomeContextStore {
	rows: Array<HomeContextRow> = [];
	digest: HomeDigestResult | null = null;
	target: HomeContextRow | null = null;
	answerVisible = false;
	/** Home is the whole conversation (the thread view): the corner steps aside, like in the DM. */
	conversationOpen = false;
	/** The Assistant DM's ack when the conversation opened: later messages sit under "N new". */
	conversationNewAfter: string | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get isEmpty(): boolean {
		return this.rows.length === 0 && this.digest == null;
	}

	setRows(items: ReadonlyArray<HomeFeedItem>): void {
		this.rows = items.slice(0, MAX_ROWS).map((item) => ({
			source: item.source,
			name: item.name,
			preview: item.preview,
			tag: tagText(item.tags[0]),
			unread: item.unread,
			conversation_id: item.conversationId ?? null,
			message_id: item.messageId ?? null,
		}));
	}

	setTarget(item: HomeFeedItem | null): void {
		this.target = item
			? {
					source: item.source,
					name: item.name,
					preview: item.preview,
					tag: tagText(item.tags[0]),
					unread: item.unread,
					conversation_id: item.conversationId ?? null,
					message_id: item.messageId ?? null,
				}
			: null;
	}

	showAnswer(): void {
		this.answerVisible = true;
	}

	dismissAnswer(): void {
		this.answerVisible = false;
	}

	/** Open the conversation view; `newAfter` is the DM's last read message at that moment. */
	openConversation(newAfter: string | null): void {
		this.conversationNewAfter = newAfter;
		this.conversationOpen = true;
	}

	closeConversation(): void {
		this.conversationOpen = false;
		this.conversationNewAfter = null;
	}

	setDigest(digest: HomeDigestResult | null): void {
		this.digest = digest;
	}

	/** Send the current context ahead of a question. Never throws, never waits long. */
	async push(): Promise<void> {
		if (this.isEmpty && !this.target) return;
		const target = this.target;
		const body = {
			summary: this.digest?.summary ?? null,
			highlights: this.digest?.highlights ?? [],
			rows: this.rows,
			target,
		};
		const request = http.post(Endpoints.KONVO_CLAUDE_HOME_CONTEXT, {body}).catch(() => undefined);
		const timeout = new Promise<void>((resolve) => setTimeout(resolve, PUSH_TIMEOUT_MS));
		await Promise.race([request, timeout]);
		// Do not erase a different row selected while this context request was in flight.
		if (this.target === target) this.target = null;
	}
}

export default new HomeContextStore();
